import { useNavigate, useSearch } from '@tanstack/react-router'
import { type FormEvent, useEffect, useState } from 'react'

import {
  Card,
  CardContent,
  CardFooter,
  CardHeader,
} from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Spinner } from '@/components/ui/spinner'
import { useAuth, useAuthActions } from '@/hooks/use-auth'
import { ApiError } from '@/lib/api'

/**
 * Admin login page.
 *
 * Desktop-centred card layout (max 400px). Mirrors the API shape
 * of the driver PWA's login page (POST /auth/login → tokens →
 * redirect) but visually styled for desktop with the brand mark
 * above a card.
 *
 * Two admin-specific bits:
 *   - **Role gate after login.** The auth API doesn't know the
 *     caller's intent — a `driver` role can call POST /auth/login
 *     just fine. We refuse them at the UI layer here and route
 *     them to the driver PWA instead.
 *   - **"Accès chauffeur" link** in the footer of the card so a
 *     chauffeur who lands on this page by mistake can self-route
 *     to the right app. URL is read from `VITE_DRIVER_URL`
 *     (falls back to `/` so dev still works).
 */

const DRIVER_URL =
  (import.meta.env.VITE_DRIVER_URL as string | undefined) ??
  'http://localhost:3001'

export function LoginPage() {
  const navigate = useNavigate()
  const { isAuthenticated, hydrated, user } = useAuth()
  const { login, logout } = useAuthActions()
  const search = useSearch({ strict: false }) as { redirect?: string }

  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [submitting, setSubmitting] = useState(false)

  // If we land on /login already authenticated as an admin, jump
  // straight to the dashboard. If we're authenticated as a driver
  // somehow (e.g. we logged in from another tab as a driver but
  // happen to be on this page), boot them so the next login
  // attempt doesn't keep the wrong session around.
  useEffect(() => {
    if (!hydrated) return
    if (!isAuthenticated) return
    if (user?.role === 'admin' || user?.role === 'super_admin') {
      void navigate({ to: search.redirect ?? '/planning' })
    } else if (user?.role === 'driver') {
      logout()
    }
  }, [hydrated, isAuthenticated, user, navigate, search.redirect, logout])

  async function handleSubmit(event: FormEvent<HTMLFormElement>): Promise<void> {
    event.preventDefault()
    setError(null)
    setSubmitting(true)
    try {
      await login(email, password)
    } catch (err) {
      setError(
        err instanceof ApiError
          ? err.message
          : 'Connexion impossible. Vérifiez votre connexion.',
      )
    } finally {
      setSubmitting(false)
    }
    // The role gate in the effect above handles the success path.
  }

  return (
    <div className="min-h-screen grid place-items-center bg-surface-muted px-4">
      <div className="w-full max-w-md flex flex-col items-center">
        {/* Brand block */}
        <div className="mb-6 text-center">
          <div
            aria-hidden="true"
            className="inline-flex items-center justify-center w-14 h-14 rounded-xl bg-brand text-brand-fg text-xl font-bold shadow"
          >
            A
          </div>
          <h1 className="mt-3 text-2xl font-bold text-text">Avity</h1>
          <p className="text-sm text-text-muted">Espace administrateur</p>
        </div>

        <Card className="w-full">
          <form onSubmit={handleSubmit} noValidate>
            <CardHeader>
              <h2 className="text-base font-semibold text-text">Connexion</h2>
              <p className="text-sm text-text-muted">
                Entrez vos identifiants pour accéder à l'administration.
              </p>
            </CardHeader>
            <CardContent className="flex flex-col gap-4">
              <div className="flex flex-col gap-1.5">
                <Label htmlFor="login-email">Email</Label>
                <Input
                  id="login-email"
                  type="email"
                  autoComplete="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  disabled={submitting}
                />
              </div>
              <div className="flex flex-col gap-1.5">
                <Label htmlFor="login-password">Mot de passe</Label>
                <Input
                  id="login-password"
                  type="password"
                  autoComplete="current-password"
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  disabled={submitting}
                />
              </div>
              {error !== null ? (
                <p
                  role="alert"
                  className="text-sm text-danger font-medium text-center"
                >
                  {error}
                </p>
              ) : null}
              <Button
                type="submit"
                size="lg"
                className="w-full"
                disabled={submitting || email === '' || password === ''}
              >
                {submitting ? <Spinner label="Connexion en cours" /> : 'Se connecter'}
              </Button>
            </CardContent>
            <CardFooter className="!justify-center">
              <a
                href={DRIVER_URL}
                className="text-xs text-text-muted hover:text-brand transition-colors"
              >
                Accès chauffeur →
              </a>
            </CardFooter>
          </form>
        </Card>
      </div>
    </div>
  )
}
